import React, { useState, useCallback, useRef, useEffect, useLayoutEffect, useMemo } from 'react';
import { IconMapPin, IconUserStar, IconSparkles } from '@tabler/icons-react';
import { Users, Check, X, Plus, Network, RefreshCw, ChevronLeft, LayoutPanelLeft, Columns3, GitBranch, Search, Settings2, Pencil, Trash2, UserPlus } from 'lucide-react';
import { BoardToolbarExpandSearch } from '../../board/toolbar/BoardToolbarExpandSearch';
import { useEquipe2TeamData } from '../hooks/useEquipe2TeamData';
import { useCoordinatorPhotos } from '../hooks/useCoordinatorPhotos';
import { useEquipe2GestaoFilterContext } from '../hooks/useEquipe2GestaoFilterContext';
import { CoordinatorAvatar } from './CoordinatorAvatar';
import { getInitials, getCoordColor, buildAbbrFromName } from '../utils/equipe2Helpers';
import {
  EMPTY_EQUIPE2_TECH_FILTERS,
  areaMatchesStructFilters,
  buildCargoFilterOptions,
  buildMarcaFilterOptions,
  buildTecnologiaFilterOptions,
  areaMatchesBoardSearch,
  countEquipe2TechFilters,
  filterTecnicosList,
  hasEquipe2TechLevelFilter,
  isTechInactive,
} from '../utils/equipe2TechFilterHelpers';
import { createHierarchyFilterSuggestionsFetcher } from '../utils/equipe2HierarchyHelpers';
import { Equipe2TechFilters } from './Equipe2TechFilters';
import { Equipe2Skeleton } from './Equipe2Skeleton';
import '../../board/toolbar/BoardClickUpFilterChip.css';
import '../../board/toolbar/BoardToolbarSearchFilters.css';
import '../../board/components/OpenColumnCollapse.css';
import './Equipe2View.css';
import { Equipe2HierarchyModal } from './Equipe2HierarchyModal';
import { Equipe2TechDetailDrawer } from './Equipe2TechDetailDrawer';
import { GoHoverTooltip } from '../../go-config/components/GoHoverTooltip';
import { useCxExpToast } from '../../customer-experience/components/CxExpSonnerToast';
import { openConfigOryaAssistant } from '../../config-assistant';
import '../../config-assistant/ConfigOryaAssistant.css';

const EQUIPE2_VIEW_OPTIONS = [
  { id: 'columns', label: 'Colunas', icon: Columns3 },
  { id: 'hierarchy', label: 'Hierarquia', icon: GitBranch },
];

function Equipe2Tooltip({ text, position = 'bottom' }) {
  return (
    <span className={`board-clickup-tooltip board-clickup-tooltip--${position}`} role="tooltip">
      {text}
    </span>
  );
}

function Equipe2TooltipBtn({
  tooltip,
  tooltipPosition = 'bottom',
  className = '',
  children,
  ...props
}) {
  return (
    <button
      type="button"
      className={['equipe2-icon-btn', 'board-clickup-icon-btn-anchor', className].filter(Boolean).join(' ')}
      {...props}
    >
      {children}
      <Equipe2Tooltip text={tooltip} position={tooltipPosition} />
    </button>
  );
}

function Equipe2Abbr({ children }) {
  return (
    <span className="equipe2-abbr">
      {children}
    </span>
  );
}

function ConfirmModal({ message, onConfirm, onCancel }) {
  return (
    <div className="equipe2-overlay" role="dialog" aria-modal="true">
      <div className="equipe2-modal">
        <div className="equipe2-modal-title">Confirmar ação</div>
        <div className="equipe2-modal-text">{message}</div>
        <div className="equipe2-modal-actions equipe2-modal-actions--form">
          <button type="button" className="user-form-link-btn user-form-footer-btn" onClick={onCancel}>Cancelar</button>
          <button type="button" className="user-form-profile-save-btn user-form-footer-btn" onClick={onConfirm}>Confirmar</button>
        </div>
      </div>
    </div>
  );
}

function EditAreaModal({ name, abbr, error, onNameChange, onAbbrChange, onSave, onCancel }) {
  return (
    <div className="equipe2-overlay" role="dialog" aria-modal="true">
      <div className="equipe2-modal">
        <div className="equipe2-modal-title">Editar área</div>
        <div className="equipe2-form-group">
          <label htmlFor="eq2-edit-name">Nome</label>
          <input
            id="eq2-edit-name"
            className="equipe2-form-input"
            value={name}
            onChange={(e) => onNameChange(e.target.value)}
            autoFocus
          />
        </div>
        <div className="equipe2-form-group">
          <label htmlFor="eq2-edit-abbr">Abreviação (máx. 4 caracteres)</label>
          <input
            id="eq2-edit-abbr"
            className="equipe2-form-input equipe2-form-input--abbr"
            value={abbr}
            maxLength={4}
            onChange={(e) => onAbbrChange(e.target.value.toUpperCase().slice(0, 4))}
          />
        </div>
        {error && <div className="equipe2-form-error">{error}</div>}
        <div className="equipe2-modal-actions equipe2-modal-actions--form">
          <button type="button" className="user-form-link-btn user-form-footer-btn" onClick={onCancel}>Cancelar</button>
          <button type="button" className="user-form-profile-save-btn user-form-footer-btn" onClick={onSave}>Salvar</button>
        </div>
      </div>
    </div>
  );
}

function TechTag({ techId, name, onRemove, nested, onManage, compactActions = false }) {
  const variantClass = nested ? 'equipe2-tech-tag--nested' : 'equipe2-tech-tag--area';
  const actionsClass = compactActions
    ? 'equipe2-tech-tag-actions equipe2-tech-tag-actions--compact'
    : 'equipe2-tech-tag-actions';
  return (
    <span className={`equipe2-tech-tag ${variantClass}`} data-tech-id={techId}>
      <span className="equipe2-tech-tag-label">{name}</span>
      <span className={actionsClass}>
        <button
          type="button"
          className="equipe2-tech-tag-manage board-clickup-icon-btn-anchor"
          onClick={(e) => {
            e.stopPropagation();
            onManage?.(techId);
          }}
          aria-label={`Gerir técnico ${name}`}
        >
          <Settings2 size={10} strokeWidth={2.2} aria-hidden />
          <Equipe2Tooltip text="Gerir técnico" position="bottom" />
        </button>
        <button
          type="button"
          className="equipe2-tech-tag-remove board-clickup-icon-btn-anchor"
          onClick={onRemove}
          aria-label={`Remover ${name}`}
        >
          ✕
          <Equipe2Tooltip text="Excluir técnico da área" />
        </button>
      </span>
    </span>
  );
}

function measureTechTagLines(container) {
  if (!container) return { hasOverflow: false, hiddenCount: 0, lineByTechId: {} };
  const tags = Array.from(container.querySelectorAll('.equipe2-tech-tag'));
  if (!tags.length) return { hasOverflow: false, hiddenCount: 0, lineByTechId: {} };

  const lineTops = [...new Set(tags.map((tag) => tag.offsetTop))].sort((a, b) => a - b);
  const topToLine = new Map(lineTops.map((top, index) => [top, index]));
  const lineByTechId = {};

  tags.forEach((tag) => {
    const techId = tag.dataset.techId;
    if (!techId) return;
    lineByTechId[techId] = topToLine.get(tag.offsetTop) ?? 0;
  });

  if (lineTops.length <= 3) {
    return { hasOverflow: false, hiddenCount: 0, lineByTechId };
  }

  const hiddenCount = tags.filter((tag) => (topToLine.get(tag.offsetTop) ?? 0) >= 3).length;
  return { hasOverflow: true, hiddenCount, lineByTechId };
}

function AreaTechTagsBlock({
  techs,
  nested,
  compactActions = false,
  collapsible,
  expanded,
  onToggleExpanded,
  onManage,
  onRemoveTech,
}) {
  const tagsRef = useRef(null);
  const [overflow, setOverflow] = useState({ hasOverflow: false, hiddenCount: 0, lineByTechId: {} });
  const [measureAll, setMeasureAll] = useState(true);

  const collapsed = collapsible && overflow.hasOverflow && !expanded;
  const visibleTechs = (!collapsed || measureAll)
    ? techs
    : techs.filter((tech) => (overflow.lineByTechId[tech.id] ?? 0) < 3);

  const runMeasure = useCallback(() => {
    if (!collapsible) {
      setOverflow({ hasOverflow: false, hiddenCount: 0, lineByTechId: {} });
      setMeasureAll(false);
      return;
    }
    setOverflow(measureTechTagLines(tagsRef.current));
    setMeasureAll(false);
  }, [collapsible, techs]);

  useLayoutEffect(() => {
    if (collapsible) setMeasureAll(true);
  }, [collapsible, techs]);

  useLayoutEffect(() => {
    if (!collapsible || !measureAll) return;
    runMeasure();
  }, [collapsible, measureAll, runMeasure, techs]);

  useLayoutEffect(() => {
    const el = tagsRef.current;
    if (!el || !collapsible) return undefined;

    const observer = new ResizeObserver(() => setMeasureAll(true));
    observer.observe(el);
    return () => observer.disconnect();
  }, [collapsible]);

  return (
    <>
      <div ref={tagsRef} className="equipe2-tech-tags">
        {visibleTechs.map((t) => (
          <TechTag
            key={t.id}
            techId={t.id}
            name={t.nome}
            nested={nested}
            compactActions={compactActions}
            onManage={onManage}
            onRemove={onRemoveTech(t.id)}
          />
        ))}
        {techs.length === 0 && <div className="equipe2-drop-hint">Arraste um técnico aqui</div>}
      </div>
      {collapsible && overflow.hasOverflow ? (
        <button
          type="button"
          className="equipe2-link-btn"
          onClick={(e) => {
            e.stopPropagation();
            onToggleExpanded();
          }}
        >
          {expanded ? 'Mostrar menos' : `Mostrar mais ${overflow.hiddenCount}`}
        </button>
      ) : null}
    </>
  );
}

function Equipe2View({ apiBaseUrl }) {
  const resolvedApiBase = apiBaseUrl || (() => {
    const h = window.location.hostname;
    if (h === 'localhost' || h === '127.0.0.1') return 'http://localhost:3002';
    if (h === '177.102.48.21' || h === '45.168.203.209') return `http://${h}:3002`;
    return window.location.origin;
  })();

  const {
    teamData,
    loading,
    errors,
    refresh,
    createArea,
    updateArea,
    deleteArea,
    linkTechnicianToArea,
    unlinkTechnician,
    linkAreaToCoordinator,
    unlinkAreaFromCoordinator,
  } = useEquipe2TeamData(resolvedApiBase);

  const coordinatorPhotos = useCoordinatorPhotos(resolvedApiBase, teamData.coordenadores);

  const [view, setView] = useState('columns');
  const [viewMenuOpen, setViewMenuOpen] = useState(false);
  const viewMenuRef = useRef(null);
  const addMenuRef = useRef(null);
  const [search, setSearch] = useState('');
  const [techSearchOpen, setTechSearchOpen] = useState(false);
  const techSearchInputRef = useRef(null);
  const [areaSearch, setAreaSearch] = useState('');
  const [newAreaName, setNewAreaName] = useState('');
  const [newAreaAbbr, setNewAreaAbbr] = useState('');
  const [addAreaOpen, setAddAreaOpen] = useState(false);
  const [addMenuOpen, setAddMenuOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [dragging, setDragging] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [editModal, setEditModal] = useState(null);
  const [hierarchyOpen, setHierarchyOpen] = useState(false);
  const [techDrawer, setTechDrawer] = useState(null);
  const [nativeMode, setNativeMode] = useState(false);
  const [techForm, setTechForm] = useState(null);
  const [expandedCoordCards, setExpandedCoordCards] = useState({});
  const [expandedAreaTechTags, setExpandedAreaTechTags] = useState({});
  const [expandedAccordionCoord, setExpandedAccordionCoord] = useState({});
  const [expandedAreaRows, setExpandedAreaRows] = useState({});
  const [openMenuAreaId, setOpenMenuAreaId] = useState(null);
  const [techPanelCollapsed, setTechPanelCollapsed] = useState(true);
  const [coordStripCollapsed, setCoordStripCollapsed] = useState({});
  const [techFilters, setTechFilters] = useState(EMPTY_EQUIPE2_TECH_FILTERS);
  const { success: toastSuccess, error: toastError, Toaster } = useCxExpToast();

  useEffect(() => {
    let cancelled = false;
    fetch(`${resolvedApiBase}/api/team-native/status`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` },
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((json) => {
        if (!cancelled) setNativeMode(Boolean(json?.native));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [resolvedApiBase]);

  const {
    gestaoList,
    gestaoByCodigo,
    espMarcas,
    espTecnologias,
    espLinks,
  } = useEquipe2GestaoFilterContext(resolvedApiBase);

  const currentViewLabel = view === 'hierarchy' ? 'Hierarquia' : 'Colunas';

  useEffect(() => {
    if (!viewMenuOpen) return undefined;
    const onDoc = (e) => {
      if (viewMenuRef.current?.contains(e.target)) return;
      setViewMenuOpen(false);
    };
    document.addEventListener('mousedown', onDoc, true);
    return () => document.removeEventListener('mousedown', onDoc, true);
  }, [viewMenuOpen]);

  useEffect(() => {
    if (!addMenuOpen) return undefined;
    const onDoc = (e) => {
      if (addMenuRef.current?.contains(e.target)) return;
      setAddMenuOpen(false);
    };
    document.addEventListener('mousedown', onDoc, true);
    return () => document.removeEventListener('mousedown', onDoc, true);
  }, [addMenuOpen]);

  useEffect(() => {
    if (!openMenuAreaId) return undefined;
    const onDoc = (e) => {
      if (e.target.closest('.equipe2-menu-wrap')) return;
      setOpenMenuAreaId(null);
    };
    document.addEventListener('mousedown', onDoc, true);
    return () => document.removeEventListener('mousedown', onDoc, true);
  }, [openMenuAreaId]);

  useEffect(() => {
    if (!techSearchOpen) return undefined;
    const id = window.requestAnimationFrame(() => techSearchInputRef.current?.focus());
    return () => window.cancelAnimationFrame(id);
  }, [techSearchOpen]);

  const { areas, coordenadores, tecnicos } = teamData;

  const fabricanteFilterOptions = useMemo(
    () => buildMarcaFilterOptions(espMarcas, techFilters.tecnologias, espLinks),
    [espMarcas, techFilters.tecnologias, espLinks],
  );

  const tecnologiaFilterOptions = useMemo(
    () => buildTecnologiaFilterOptions(espTecnologias, techFilters.fabricantes, espLinks),
    [espTecnologias, techFilters.fabricantes, espLinks],
  );

  const cargoFilterOptions = useMemo(
    () => buildCargoFilterOptions(gestaoList),
    [gestaoList],
  );

  const equipe2FilterContext = useMemo(() => ({
    cargoOptions: cargoFilterOptions,
    fabricanteOptions: fabricanteFilterOptions,
    tecnologiaOptions: tecnologiaFilterOptions,
  }), [cargoFilterOptions, fabricanteFilterOptions, tecnologiaFilterOptions]);

  const fetchFilterSuggestions = useMemo(
    () => createHierarchyFilterSuggestionsFetcher({
      coordenadores,
      areas,
      tecnicos,
      gestaoByCodigo,
    }),
    [coordenadores, areas, tecnicos, gestaoByCodigo],
  );

  useEffect(() => {
    setTechFilters((prev) => {
      const allowedFab = new Set(fabricanteFilterOptions.map((o) => o.value));
      const nextFab = prev.fabricantes.filter((id) => allowedFab.has(String(id)));
      const allowedTec = new Set(tecnologiaFilterOptions.map((o) => o.value));
      const nextTec = prev.tecnologias.filter((id) => allowedTec.has(String(id)));
      if (
        nextFab.length === prev.fabricantes.length
        && nextTec.length === prev.tecnologias.length
      ) {
        return prev;
      }
      return { ...prev, fabricantes: nextFab, tecnologias: nextTec };
    });
  }, [fabricanteFilterOptions, tecnologiaFilterOptions]);

  const getTecnicosByArea = useCallback((areaId) => filterTecnicosList(
    tecnicos.filter((t) => t.areaId === areaId),
    techFilters,
    gestaoByCodigo,
    espLinks,
  ), [tecnicos, techFilters, gestaoByCodigo, espLinks]);
  const getUnassignedAreas = () => areas.filter((a) => a.coordenadorId === null);
  const getAreasByCoordinator = (coordId) => areas.filter((a) => a.coordenadorId === coordId);
  const getAvailableTechnicians = () =>
    tecnicos.filter((t) => t.areaId === null && (techFilters.showInactive || !isTechInactive(t)));

  const availableAll = getAvailableTechnicians();
  const availableFiltered = filterTecnicosList(availableAll, techFilters, gestaoByCodigo, espLinks)
    .filter((t) => t.nome.toLowerCase().includes(search.trim().toLowerCase()));

  const draggingTechnician = dragging?.type === 'technician';
  const draggingArea = dragging?.type === 'area';

  const handleDragStart = (type, id) => (e) => {
    e.dataTransfer.effectAllowed = 'move';
    try { e.dataTransfer.setData('text/plain', id); } catch (err) { /* noop */ }
    setDragging({ type, id });
  };

  const handleDragEnd = () => {
    setDragging(null);
    setDragOverId(null);
  };

  const handleDragOver = (e) => e.preventDefault();
  const handleDragEnter = (id) => (e) => {
    e.preventDefault();
    setDragOverId(id);
  };
  const handleDragLeave = (id) => () => {
    setDragOverId((prev) => (prev === id ? null : prev));
  };

  const handleDropOnArea = (areaId) => (e) => {
    e.preventDefault();
    setDragOverId(null);
    if (!dragging || dragging.type !== 'technician') return;
    const tech = tecnicos.find((t) => t.id === dragging.id);
    const area = areas.find((a) => a.id === areaId);
    if (!tech || !area || tech.areaId === areaId) return;
    setConfirm({
      type: 'assign-tech',
      techId: tech.id,
      areaId,
      message: `Vincular "${tech.nome}" à área "${area.nome}"?`,
    });
    setDragging(null);
  };

  const handleDropOnCoordinator = (coordId) => (e) => {
    e.preventDefault();
    setDragOverId(null);
    if (!dragging || dragging.type !== 'area') return;
    const area = areas.find((a) => a.id === dragging.id);
    const coord = coordenadores.find((c) => c.id === coordId);
    if (!area || !coord || area.coordenadorId === coordId) return;
    setConfirm({
      type: 'assign-area',
      areaId: area.id,
      coordId,
      message: `Vincular a área "${area.nome}" ao coordenador "${coord.nome}"?`,
    });
    setDragging(null);
  };

  const confirmYes = async () => {
    if (!confirm) return;
    try {
      if (confirm.type === 'assign-tech') {
        await linkTechnicianToArea(confirm.techId, confirm.areaId);
        toastSuccess('Técnico vinculado à área.');
      } else if (confirm.type === 'assign-area') {
        await linkAreaToCoordinator(confirm.areaId, confirm.coordId);
        toastSuccess('Área vinculada ao coordenador.');
      } else if (confirm.type === 'unlink-area') {
        await unlinkAreaFromCoordinator(confirm.areaId);
        toastSuccess('Área devolvida para a lista sem coordenador.');
      } else if (confirm.type === 'delete-area') {
        await deleteArea(confirm.areaId);
        toastSuccess('Área excluída.');
      }
    } catch (error) {
      toastError(error.message || 'Erro ao executar ação.');
    }
    setConfirm(null);
  };

  const handleCreateArea = async () => {
    const name = newAreaName.trim();
    if (!name || isCreating) return;
    setIsCreating(true);
    try {
      const created = await createArea(name);
      const abbr = newAreaAbbr.trim().slice(0, 4);
      if (abbr && abbr !== (created.abreviacao || '')) {
        await updateArea(created.id, name, abbr);
      }
      setNewAreaName('');
      setNewAreaAbbr('');
      setAddAreaOpen(false);
      toastSuccess(`Área "${name}" cadastrada.`);
    } catch (error) {
      toastError(error.message || 'Erro ao criar área.');
    } finally {
      setIsCreating(false);
    }
  };

  const cancelAddArea = () => {
    setAddAreaOpen(false);
    setNewAreaName('');
    setNewAreaAbbr('');
  };

  const handleRemoveTech = (techId) => async (e) => {
    e.stopPropagation();
    try {
      await unlinkTechnician(techId);
    } catch (error) {
      toastError(error.message || 'Erro ao remover técnico.');
    }
  };

  const handleRequestDelete = (area) => (e) => {
    e.stopPropagation();
    const count = getTecnicosByArea(area.id).length;
    if (count > 0 || area.coordenadorId) return;
    setConfirm({
      type: 'delete-area',
      areaId: area.id,
      message: `Excluir a área "${area.nome}"? Essa ação não pode ser desfeita.`,
    });
  };

  const handleRequestUnlink = (area) => (e) => {
    e.stopPropagation();
    setOpenMenuAreaId(null);
    setConfirm({
      type: 'unlink-area',
      areaId: area.id,
      message: `Desvincular a área "${area.nome}" do coordenador?`,
    });
  };

  const handleSaveEdit = async () => {
    if (!editModal?.name?.trim()) {
      setEditModal((m) => ({ ...m, error: 'Nome é obrigatório.' }));
      return;
    }
    try {
      await updateArea(editModal.areaId, editModal.name, editModal.abbr);
      setEditModal(null);
      toastSuccess('Área atualizada.');
    } catch (error) {
      setEditModal((m) => ({ ...m, error: error.message || 'Erro ao salvar.' }));
    }
  };

  const renderAreaContent = (area, { topLevel, nested, showMenu }) => {
    const techs = getTecnicosByArea(area.id);
    const isDropTarget = draggingTechnician && dragOverId === area.id;
    const canDelete = techs.length === 0 && !area.coordenadorId;
    const abbr = area.abreviacao || '—';

    return (
      <>
        <div className="equipe2-card-head">
          <div className="equipe2-card-head-left">
            <Equipe2Abbr>{abbr}</Equipe2Abbr>
            <span className="equipe2-card-name equipe2-card-name--area" title={area.nome}>{area.nome}</span>
            <span className="equipe2-card-count">{techs.length}</span>
          </div>
          {showMenu ? (
            <div className={`equipe2-menu-wrap${openMenuAreaId === area.id ? ' is-open' : ''}`}>
              <Equipe2TooltipBtn
                tooltip="Configurar área"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenMenuAreaId((prev) => (prev === area.id ? null : area.id));
                }}
              >
                ⋮
              </Equipe2TooltipBtn>
              {openMenuAreaId === area.id && (
                <div className="equipe2-menu">
                  <button type="button" onClick={handleRequestUnlink(area)}>Configurar área</button>
                </div>
              )}
            </div>
          ) : (
            <div className="equipe2-card-actions">
              <Equipe2TooltipBtn
                tooltip="Editar área"
                onClick={(e) => {
                  e.stopPropagation();
                  setEditModal({ areaId: area.id, name: area.nome, abbr: area.abreviacao || '', error: '' });
                }}
              >
                <Pencil size={14} strokeWidth={2} aria-hidden />
              </Equipe2TooltipBtn>
              <Equipe2TooltipBtn
                tooltip="Excluir área"
                className={canDelete ? 'equipe2-icon-btn--danger' : ''}
                disabled={!canDelete}
                onClick={canDelete ? handleRequestDelete(area) : undefined}
              >
                <Trash2 size={16} strokeWidth={1.75} aria-hidden />
              </Equipe2TooltipBtn>
            </div>
          )}
        </div>
        <AreaTechTagsBlock
          techs={techs}
          nested={nested}
          compactActions={topLevel || nested}
          collapsible={topLevel && !nested}
          expanded={!!expandedAreaTechTags[area.id]}
          onToggleExpanded={() => setExpandedAreaTechTags((s) => ({ ...s, [area.id]: !s[area.id] }))}
          onManage={openTechDrawer}
          onRemoveTech={handleRemoveTech}
        />
      </>
    );
  };

  const renderAreaCard = (area, { topLevel, nested, showMenu = false }) => {
    const isDropTarget = draggingTechnician && dragOverId === area.id;
    const menuOpen = showMenu && openMenuAreaId === area.id;
    const cardClass = [
      topLevel ? 'equipe2-card equipe2-card--draggable-area' : 'equipe2-nested-area',
      menuOpen ? 'is-menu-open' : '',
      isDropTarget ? 'is-drop-target' : '',
      draggingTechnician && !isDropTarget ? 'is-drag-hint' : '',
    ].filter(Boolean).join(' ');

    return (
      <div
        key={area.id}
        className={cardClass}
        draggable={topLevel}
        onDragStart={topLevel ? handleDragStart('area', area.id) : undefined}
        onDragEnd={topLevel ? handleDragEnd : undefined}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter(area.id)}
        onDragLeave={handleDragLeave(area.id)}
        onDrop={handleDropOnArea(area.id)}
      >
        {renderAreaContent(area, { topLevel, nested, showMenu })}
      </div>
    );
  };

  const renderHierarchyArea = (area) => {
    const techs = getTecnicosByArea(area.id);
    const expanded = !!expandedAreaRows[area.id];
    const isDropTarget = draggingTechnician && dragOverId === area.id;
    const canDelete = techs.length === 0 && !area.coordenadorId;

    return (
      <div
        key={area.id}
        className={`equipe2-accordion${isDropTarget ? ' is-drop-target' : ''}${draggingTechnician && !isDropTarget ? ' is-drag-hint' : ''}`}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter(area.id)}
        onDragLeave={handleDragLeave(area.id)}
        onDrop={handleDropOnArea(area.id)}
      >
        <div
          className="equipe2-accordion-trigger"
          onClick={() => setExpandedAreaRows((s) => ({ ...s, [area.id]: !s[area.id] }))}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && setExpandedAreaRows((s) => ({ ...s, [area.id]: !s[area.id] }))}
        >
          <div className="equipe2-card-head-left">
            <span className={`equipe2-chevron${expanded ? ' is-open' : ''}`}>›</span>
            <Equipe2Abbr>{area.abreviacao || '—'}</Equipe2Abbr>
            <span className="equipe2-card-name equipe2-card-name--area" title={area.nome}>{area.nome}</span>
            <span className="equipe2-card-count">{techs.length}</span>
          </div>
          <div className="equipe2-card-actions">
            <Equipe2TooltipBtn
              tooltip="Editar área"
              onClick={(e) => {
                e.stopPropagation();
                setEditModal({ areaId: area.id, name: area.nome, abbr: area.abreviacao || '', error: '' });
              }}
            >
              <Pencil size={14} strokeWidth={2} aria-hidden />
            </Equipe2TooltipBtn>
            <Equipe2TooltipBtn
              tooltip="Excluir área"
              className={canDelete ? 'equipe2-icon-btn--danger' : ''}
              disabled={!canDelete}
              onClick={canDelete ? handleRequestDelete(area) : undefined}
            >
              <Trash2 size={16} strokeWidth={1.75} aria-hidden />
            </Equipe2TooltipBtn>
          </div>
        </div>
        {expanded && (
          <div className="equipe2-accordion-body">
            {techs.map((t) => (
              <TechTag
                key={t.id}
                techId={t.id}
                name={t.nome}
                onManage={openTechDrawer}
                onRemove={handleRemoveTech(t.id)}
              />
            ))}
            {techs.length === 0 && <div className="equipe2-drop-hint">Arraste um técnico aqui</div>}
          </div>
        )}
      </div>
    );
  };

  const areaSearchLower = areaSearch.trim().toLowerCase();

  const hasTeamTechFilters = countEquipe2TechFilters(techFilters) > 0;

  const areaVisibleInTeam = useCallback((area) => {
    if (!areaMatchesBoardSearch(area, areaSearchLower, tecnicos, coordenadores)) return false;
    if (!areaMatchesStructFilters(area, techFilters, coordenadores)) return false;
    if (!hasEquipe2TechLevelFilter(techFilters)) return true;
    return getTecnicosByArea(area.id).length > 0;
  }, [techFilters, coordenadores, tecnicos, getTecnicosByArea, areaSearchLower]);

  const unassignedAreas = getUnassignedAreas().filter(areaVisibleInTeam);

  const columnsCoordEntries = useMemo(() => (
    coordenadores
      .map((coord, originalIdx) => {
        const coordAreas = getAreasByCoordinator(coord.id).filter(areaVisibleInTeam);
        return { coord, originalIdx, coordAreas };
      })
      .filter(({ coord, coordAreas }) => {
        if (coordAreas.length > 0) return true;
        if (areaSearchLower) {
          return String(coord.nome || '').toLowerCase().includes(areaSearchLower);
        }
        if (hasTeamTechFilters) return false;
        return true;
      })
      .sort((a, b) => {
        const aHasAreas = a.coordAreas.length > 0;
        const bHasAreas = b.coordAreas.length > 0;
        if (aHasAreas !== bHasAreas) return aHasAreas ? -1 : 1;
        return a.originalIdx - b.originalIdx;
      })
  ), [coordenadores, getAreasByCoordinator, areaVisibleInTeam, areaSearchLower, hasTeamTechFilters]);

  const openTechDrawer = useCallback((techId) => {
    const tech = tecnicos.find((t) => t.id === techId);
    if (!tech) return;
    const area = areas.find((a) => a.id === tech.areaId);
    const coord = area?.coordenadorId != null
      ? coordenadores.find((c) => c.id === area.coordenadorId)
      : null;
    const coordIdx = coord ? coordenadores.findIndex((c) => c.id === coord.id) : 0;
    const color = getCoordColor(coordIdx >= 0 ? coordIdx : 0);
    setTechDrawer({
      techId: tech.id,
      name: tech.nome,
      inactive: isTechInactive(tech),
      areaName: area?.nome || '',
      coordinatorName: coord?.nome || '',
      areaTechCount: area ? tecnicos.filter((t) => t.areaId === area.id).length : null,
      colorSoft: color.soft,
      colorStrong: color.strong,
    });
  }, [tecnicos, areas, coordenadores]);

  return (
    <div className="equipe2-root">
      {Toaster}

      <header className="equipe2-header">
        <div className="equipe2-header-row">
          <nav className="equipe2-breadcrumb" aria-label="Localização em cadastros">
            <span className="equipe2-breadcrumb-avatar" aria-hidden="true">C</span>
            <span className="equipe2-breadcrumb-segment equipe2-breadcrumb-segment--parent">Cadastros</span>
            <span className="equipe2-breadcrumb-sep" aria-hidden="true">/</span>
            <span className="equipe2-breadcrumb-current" aria-current="page">
              <Users size={13} strokeWidth={1.75} aria-hidden className="equipe2-breadcrumb-current-icon" />
              <span className="equipe2-breadcrumb-current-text">Gestão de equipe</span>
            </span>
          </nav>
          <button
            type="button"
            className="config-orya-nav-btn equipe2-orya-nav-btn"
            onClick={openConfigOryaAssistant}
            aria-label="Pergunte a IA"
          >
            <IconSparkles size={15} stroke={1.75} aria-hidden />
            <span className="config-orya-nav-btn-label">Pergunte a IA</span>
          </button>
        </div>
      </header>

      <div className="equipe2-toolbar">
        <div className="equipe2-toolbar-left">
          <span className="board-clickup-filter-chip equipe2-summary-chip" role="status">
            <span className="board-clickup-filter-chip-text equipe2-summary-chip-text">
              <span className="equipe2-summary-stat">
                <span className="board-clickup-filter-chip-icon" aria-hidden="true">
                  <IconMapPin size={13} stroke={1.5} />
                </span>
                <span className="board-clickup-filter-chip-label">áreas</span>
                <span className="equipe2-summary-count-badge">{areas.length}</span>
              </span>
              <span className="equipe2-summary-chip-sep" aria-hidden="true">•</span>
              <span className="equipe2-summary-stat">
                <span className="board-clickup-filter-chip-icon" aria-hidden="true">
                  <IconUserStar size={13} stroke={1.5} />
                </span>
                <span className="board-clickup-filter-chip-label">coordenadores</span>
                <span className="equipe2-summary-count-badge">{coordenadores.length}</span>
              </span>
            </span>
          </span>
          <span className="board-toolbar-separator" aria-hidden="true">|</span>
          <BoardToolbarExpandSearch
            value={areaSearch}
            onChange={setAreaSearch}
            placeholder="Buscar área, técnico ou coordenador..."
            ariaLabel="Buscar área, técnico ou coordenador"
          />
          <Equipe2TechFilters
            filters={techFilters}
            onChange={setTechFilters}
            onFetchSuggestions={fetchFilterSuggestions}
            filterContext={equipe2FilterContext}
          />
          <div className="board-clickup-filter-chip-anchor equipe2-add-area-anchor" ref={nativeMode ? addMenuRef : undefined}>
            {nativeMode ? (
              <>
                <GoHoverTooltip
                  text={addMenuOpen || techDrawer || techForm?.mode === 'create' ? '' : 'Adicionar'}
                  className="go-hover-tip-wrap--contents"
                  align="center"
                  delayMs={180}
                >
                  <button
                    type="button"
                    className={['equipe2-add-btn', 'equipe2-add-btn--attention', addMenuOpen ? 'is-open' : ''].filter(Boolean).join(' ')}
                    aria-expanded={addMenuOpen}
                    aria-haspopup="menu"
                    aria-label="Adicionar"
                    onClick={() => setAddMenuOpen((v) => !v)}
                  >
                    <span className="equipe2-add-btn-plus">+</span>
                  </button>
                </GoHoverTooltip>
                {addMenuOpen ? (
                  <div className="equipe2-view-menu equipe2-add-menu" role="menu" aria-label="Adicionar">
                    <button
                      type="button"
                      className="equipe2-view-menu-item"
                      role="menuitem"
                      onClick={() => {
                        setAddMenuOpen(false);
                        setAddAreaOpen(true);
                      }}
                    >
                      <Plus size={16} strokeWidth={1.75} aria-hidden />
                      <span className="equipe2-view-menu-item-label">Nova área</span>
                    </button>
                    <button
                      type="button"
                      className="equipe2-view-menu-item"
                      role="menuitem"
                      onClick={() => {
                        setAddMenuOpen(false);
                        setTechDrawer(null);
                        setTechForm({ mode: 'create' });
                      }}
                    >
                      <UserPlus size={16} strokeWidth={1.75} aria-hidden />
                      <span className="equipe2-view-menu-item-label">Novo técnico</span>
                    </button>
                  </div>
                ) : null}
              </>
            ) : (
              <button
                type="button"
                className={['board-clickup-filter-chip', addAreaOpen ? 'is-open' : ''].filter(Boolean).join(' ')}
                aria-expanded={addAreaOpen}
                aria-label="Adicionar área"
                onClick={() => setAddAreaOpen((v) => !v)}
              >
                <span className="board-clickup-filter-chip-icon" aria-hidden="true">
                  <Plus size={13} strokeWidth={1.75} />
                </span>
                <span className="board-clickup-filter-chip-text">
                  <span className="board-clickup-filter-chip-label">Adicionar área</span>
                </span>
              </button>
            )}
            {addAreaOpen && (
              <div className="equipe2-add-area-form">
                <input
                  className="equipe2-add-area-input"
                  placeholder="Nome da área"
                  value={newAreaName}
                  maxLength={100}
                  onChange={(e) => {
                    const name = e.target.value;
                    setNewAreaName(name);
                    if (!newAreaAbbr.trim()) {
                      setNewAreaAbbr(buildAbbrFromName(name));
                    }
                  }}
                  onKeyDown={(e) => e.key === 'Enter' && handleCreateArea()}
                  autoFocus
                />
                <input
                  className="equipe2-add-area-input equipe2-add-area-input--abbr"
                  placeholder="Abrev."
                  value={newAreaAbbr}
                  maxLength={4}
                  onChange={(e) => setNewAreaAbbr(e.target.value.toUpperCase().slice(0, 4))}
                  onKeyDown={(e) => e.key === 'Enter' && handleCreateArea()}
                />
                <button
                  type="button"
                  className="equipe2-add-area-action equipe2-add-area-action--confirm"
                  disabled={!newAreaName.trim() || isCreating}
                  onClick={handleCreateArea}
                  title="Confirmar"
                  aria-label="Confirmar cadastro"
                >
                  <Check size={14} strokeWidth={2.25} />
                </button>
                <button
                  type="button"
                  className="equipe2-add-area-action"
                  onClick={cancelAddArea}
                  title="Cancelar"
                  aria-label="Cancelar cadastro"
                >
                  <X size={14} strokeWidth={2.25} />
                </button>
              </div>
            )}
          </div>
        </div>
        <div className="equipe2-toolbar-right">
          <div className="equipe2-view-menu-anchor" ref={viewMenuRef}>
            <button
              type="button"
              className={['board-clickup-filter-chip', 'has-selection', viewMenuOpen ? 'is-open' : ''].filter(Boolean).join(' ')}
              aria-expanded={viewMenuOpen}
              aria-haspopup="menu"
              aria-label="Selecionar visão"
              onClick={() => setViewMenuOpen((open) => !open)}
            >
              <span className="board-clickup-filter-chip-icon" aria-hidden="true">
                <LayoutPanelLeft size={13} strokeWidth={1.75} />
              </span>
              <span className="board-clickup-filter-chip-text">
                <span className="board-clickup-filter-chip-label">Visão</span>
                <span className="board-clickup-filter-chip-separator">:</span>
                <span className="board-clickup-filter-chip-value">{currentViewLabel}</span>
              </span>
            </button>
            {viewMenuOpen ? (
              <div className="equipe2-view-menu" role="menu" aria-label="Opções de visão">
                {EQUIPE2_VIEW_OPTIONS.map((option) => {
                  const OptionIcon = option.icon;
                  const isActive = view === option.id;
                  return (
                    <button
                      key={option.id}
                      type="button"
                      className={['equipe2-view-menu-item', isActive ? 'is-active' : ''].filter(Boolean).join(' ')}
                      role="menuitemradio"
                      aria-checked={isActive}
                      onClick={() => {
                        setView(option.id);
                        setViewMenuOpen(false);
                      }}
                    >
                      <OptionIcon size={16} strokeWidth={1.75} aria-hidden />
                      <span className="equipe2-view-menu-item-label">{option.label}</span>
                      {isActive ? <Check size={14} strokeWidth={2.25} aria-hidden className="equipe2-view-menu-item-check" /> : null}
                    </button>
                  );
                })}
              </div>
            ) : null}
          </div>
          <button
            type="button"
            className="board-clickup-filter-chip"
            aria-label="Estrutura da equipe"
            onClick={() => setHierarchyOpen(true)}
          >
            <span className="board-clickup-filter-chip-icon" aria-hidden="true">
              <Network size={13} strokeWidth={1.75} />
            </span>
            <span className="board-clickup-filter-chip-text">
              <span className="board-clickup-filter-chip-label">Estrutura da equipe</span>
            </span>
          </button>
          <button
            type="button"
            className="board-clickup-filter-chip"
            aria-label="Atualizar equipe"
            onClick={() => refresh().then(() => toastSuccess('Lista atualizada.'))}
          >
            <span className="board-clickup-filter-chip-icon" aria-hidden="true">
              <RefreshCw size={13} strokeWidth={1.75} />
            </span>
            <span className="board-clickup-filter-chip-text">
              <span className="board-clickup-filter-chip-label">Atualizar</span>
            </span>
          </button>
        </div>
      </div>

      {loading ? (
        <Equipe2Skeleton panelCollapsed={techPanelCollapsed} />
      ) : (
        <div className={`equipe2-grid equipe2-grid--${techPanelCollapsed ? 'panel-collapsed' : 'panel-open'}`}>
          <div className="equipe2-main">
            {errors.general && <div className="equipe2-form-error" style={{ marginBottom: 12 }}>{errors.general}</div>}

            {view === 'columns' && (
              <>
                <section className="equipe2-section">
                  <div className="equipe2-section-title">
                    <h2>Áreas</h2>
                    <span className="equipe2-section-count">{unassignedAreas.length}</span>
                  </div>
                  <div className="equipe2-areas-grid">
                    {unassignedAreas.map((area) => renderAreaCard(area, { topLevel: true, nested: false }))}
                    {unassignedAreas.length === 0 && !errors.areas && (
                      <div className="equipe2-empty">Nenhuma área sem coordenador</div>
                    )}
                  </div>
                </section>

                <section className="equipe2-section">
                  <div className="equipe2-section-title">
                    <h2>Coordenadores</h2>
                    <span className="equipe2-section-count">{coordenadores.length}</span>
                  </div>
                  <div className="equipe2-coords-grid">
                    {columnsCoordEntries.map(({ coord, originalIdx, coordAreas }) => {
                      const color = getCoordColor(originalIdx);
                      const techCount = coordAreas.reduce((sum, a) => sum + getTecnicosByArea(a.id).length, 0);
                      const expandedA = !!expandedCoordCards[coord.id];
                      const visibleAreas = expandedA ? coordAreas : coordAreas.slice(0, 3);
                      const stripCollapsed = coordStripCollapsed[coord.id] ?? (coordAreas.length === 0);
                      const isDropTarget = draggingArea && dragOverId === coord.id;

                      const handleCoordDragEnter = (e) => {
                        if (stripCollapsed && draggingArea) {
                          setCoordStripCollapsed((s) => ({ ...s, [coord.id]: false }));
                        }
                        handleDragEnter(coord.id)(e);
                      };

                      const handleCoordCollapsedDragOver = (e) => {
                        if (!draggingArea) return;
                        e.preventDefault();
                        setCoordStripCollapsed((s) => ({ ...s, [coord.id]: false }));
                        handleDragOver(e);
                      };

                      return (
                        <div
                          key={coord.id}
                          className={[
                            'equipe2-coord-collapse-wrap',
                            stripCollapsed ? 'is-collapsed' : '',
                            isDropTarget ? 'is-drop-target' : '',
                            draggingArea && !isDropTarget ? 'is-drag-hint' : '',
                          ].filter(Boolean).join(' ')}
                          onDragOver={handleDragOver}
                          onDragEnter={handleCoordDragEnter}
                          onDragLeave={handleDragLeave(coord.id)}
                          onDrop={handleDropOnCoordinator(coord.id)}
                        >
                          <div
                            className={[
                              'equipe2-card',
                              'equipe2-card--coord',
                              'equipe2-coord-expanded',
                            ].filter(Boolean).join(' ')}
                            aria-hidden={stripCollapsed}
                          >
                            <div className="equipe2-card-head">
                              <div className="equipe2-card-head-left">
                                <CoordinatorAvatar
                                  name={coord.nome}
                                  photoUrl={coordinatorPhotos[coord.id]}
                                  color={color}
                                />
                                <div style={{ minWidth: 0 }}>
                                  <div className="equipe2-card-name">{coord.nome}</div>
                                  <div className="equipe2-coord-meta">
                                    {coordAreas.length} áreas • {techCount} técnicos
                                  </div>
                                </div>
                              </div>
                              <div className="equipe2-coord-head-actions">
                                <button
                                  type="button"
                                  className="open-column-collapse-btn board-clickup-icon-btn-anchor"
                                  aria-label="Minimizar coordenador"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setCoordStripCollapsed((s) => ({ ...s, [coord.id]: true }));
                                  }}
                                >
                                  <ChevronLeft size={15.4} strokeWidth={2.2} aria-hidden />
                                  <Equipe2Tooltip text="Minimizar coordenador" position="top" />
                                </button>
                              </div>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, position: 'relative', zIndex: 2 }}>
                              {visibleAreas.map((area) => renderAreaCard(area, { topLevel: false, nested: true, showMenu: true }))}
                            </div>
                            {coordAreas.length > 3 && (
                              <button
                                type="button"
                                className="equipe2-link-btn"
                                onClick={() => setExpandedCoordCards((s) => ({ ...s, [coord.id]: !s[coord.id] }))}
                              >
                                {expandedA ? 'Mostrar menos' : `Mostrar mais ${coordAreas.length - 3}`}
                              </button>
                            )}
                            {coordAreas.length === 0 && (
                              <div className="equipe2-drop-hint">Arraste áreas aqui</div>
                            )}
                          </div>

                          <button
                            type="button"
                            className="equipe2-coord-collapsed board-clickup-icon-btn-anchor"
                            aria-label={`Expandir coordenador ${coord.nome}`}
                            aria-hidden={!stripCollapsed}
                            tabIndex={stripCollapsed ? 0 : -1}
                            onClick={() => setCoordStripCollapsed((s) => ({ ...s, [coord.id]: false }))}
                            onDragOver={handleCoordCollapsedDragOver}
                            onDrop={handleDropOnCoordinator(coord.id)}
                          >
                            <span className="equipe2-coord-collapsed-inner">
                              <CoordinatorAvatar
                                name={coord.nome}
                                photoUrl={coordinatorPhotos[coord.id]}
                                color={color}
                                size={32}
                              />
                              <span className="equipe2-coord-collapsed-name">{coord.nome}</span>
                            </span>
                            <Equipe2Tooltip text="Expandir coordenador" position="bottom" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </section>
              </>
            )}

            {view === 'hierarchy' && (
              <>
                <section className="equipe2-section">
                  <div className="equipe2-section-title">
                    <h2>Áreas sem coordenador</h2>
                    <span className="equipe2-section-count">{unassignedAreas.length}</span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {unassignedAreas.map(renderHierarchyArea)}
                  </div>
                </section>

                <section className="equipe2-section">
                  <div className="equipe2-section-title">
                    <h2>Coordenadores</h2>
                    <span className="equipe2-section-count">{coordenadores.length}</span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {coordenadores.map((coord, idx) => {
                      const color = getCoordColor(idx);
                      const coordAreas = getAreasByCoordinator(coord.id).filter(areaVisibleInTeam);
                      if (coordAreas.length === 0) {
                        if (areaSearchLower) {
                          if (!String(coord.nome || '').toLowerCase().includes(areaSearchLower)) return null;
                        } else if (hasTeamTechFilters) {
                          return null;
                        }
                      }
                      const techCount = coordAreas.reduce((sum, a) => sum + getTecnicosByArea(a.id).length, 0);
                      const expandedB = !!expandedAccordionCoord[coord.id];
                      const isDropTarget = draggingArea && dragOverId === coord.id;

                      return (
                        <div
                          key={coord.id}
                          className={`equipe2-accordion${isDropTarget ? ' is-drop-target' : ''}`}
                          onDragOver={handleDragOver}
                          onDragEnter={handleDragEnter(coord.id)}
                          onDragLeave={handleDragLeave(coord.id)}
                          onDrop={handleDropOnCoordinator(coord.id)}
                        >
                          <div
                            className="equipe2-accordion-trigger"
                            onClick={() => setExpandedAccordionCoord((s) => ({ ...s, [coord.id]: !s[coord.id] }))}
                            role="button"
                            tabIndex={0}
                          >
                            <div className="equipe2-card-head-left">
                              <span className={`equipe2-chevron${expandedB ? ' is-open' : ''}`}>›</span>
                              <CoordinatorAvatar
                                name={coord.nome}
                                photoUrl={coordinatorPhotos[coord.id]}
                                color={color}
                                size={34}
                              />
                              <div>
                                <div className="equipe2-card-name">{coord.nome}</div>
                                <div className="equipe2-coord-meta">{coordAreas.length} áreas • {techCount} técnicos</div>
                              </div>
                            </div>
                          </div>
                          {expandedB && (
                            <div style={{ padding: '2px 16px 14px', display: 'flex', flexDirection: 'column', gap: 7, borderTop: '1px solid #f1eff7' }}>
                              {coordAreas.map((area) => {
                                const techs = getTecnicosByArea(area.id);
                                const expanded = !!expandedAreaRows[area.id];
                                const menuOpen = openMenuAreaId === area.id;
                                return (
                                  <div
                                    key={area.id}
                                    className={`equipe2-nested-area${menuOpen ? ' is-menu-open' : ''}`}
                                    style={{ overflow: menuOpen ? 'visible' : 'hidden', marginTop: 8 }}
                                  >
                                    <div
                                      className="equipe2-accordion-trigger"
                                      style={{ padding: '9px 11px' }}
                                      onClick={() => setExpandedAreaRows((s) => ({ ...s, [area.id]: !s[area.id] }))}
                                    >
                                      <div className="equipe2-card-head-left">
                                        <span className={`equipe2-chevron${expanded ? ' is-open' : ''}`}>›</span>
                                        <Equipe2Abbr>{area.abreviacao || '—'}</Equipe2Abbr>
                                        <span className="equipe2-card-name equipe2-card-name--area">{area.nome}</span>
                                        <span className="equipe2-card-count">{techs.length}</span>
                                      </div>
                                      <div className={`equipe2-menu-wrap${openMenuAreaId === area.id ? ' is-open' : ''}`}>
                                        <Equipe2TooltipBtn
                                          tooltip="Configurar área"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setOpenMenuAreaId((prev) => (prev === area.id ? null : area.id));
                                          }}
                                        >
                                          ⋮
                                        </Equipe2TooltipBtn>
                                        {openMenuAreaId === area.id && (
                                          <div className="equipe2-menu">
                                            <button type="button" onClick={handleRequestUnlink(area)}>Configurar área</button>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    {expanded && (
                                      <div style={{ padding: '0 11px 11px 34px', display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                                        {techs.map((t) => (
                                          <TechTag
                                            key={t.id}
                                            techId={t.id}
                                            name={t.nome}
                                            nested
                                            compactActions
                                            onManage={openTechDrawer}
                                            onRemove={handleRemoveTech(t.id)}
                                          />
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </section>
              </>
            )}
          </div>

          <div className="equipe2-tech-rail">
            {techPanelCollapsed ? (
              <button
                type="button"
                className="equipe2-tech-strip-collapsed board-clickup-icon-btn-anchor"
                onClick={() => setTechPanelCollapsed(false)}
                aria-label={`Expandir técnicos disponíveis (${availableAll.length})`}
              >
                <span className="equipe2-tech-strip-collapsed-inner">
                  <span className="equipe2-tech-strip-collapsed-badge">
                    <Users className="equipe2-tech-strip-collapsed-badge-icon" size={12} strokeWidth={2} aria-hidden="true" />
                    <span className="equipe2-tech-strip-collapsed-label">Técnicos disponíveis</span>
                  </span>
                  <span className="equipe2-tech-strip-collapsed-count">{availableAll.length}</span>
                </span>
                <Equipe2Tooltip
                  text="Clique para exibir os técnicos ainda não vinculados"
                  position="left"
                />
              </button>
            ) : (
              <div className="equipe2-tech-panel-wrap">
                <button
                  type="button"
                  className="equipe2-tech-panel-edge-collapse open-column-collapse-btn board-clickup-icon-btn-anchor"
                  aria-label="Recolher coluna"
                  onClick={() => setTechPanelCollapsed(true)}
                >
                  <ChevronLeft size={15.4} strokeWidth={2.2} aria-hidden />
                  <span className="board-clickup-tooltip board-clickup-tooltip--bottom" role="tooltip">
                    Recolher coluna
                  </span>
                </button>
                <div className="equipe2-tech-panel">
                  <div className="equipe2-tech-panel-head">
                    <div className="equipe2-tech-panel-title-row">
                      <div className="equipe2-tech-open-title-row">
                        <span className="equipe2-tech-open-badge">
                          <Users className="equipe2-tech-open-badge-icon" size={12} strokeWidth={2} aria-hidden="true" />
                          Técnicos disponíveis
                        </span>
                        <span className="equipe2-tech-open-count">{availableAll.length}</span>
                        <button
                          type="button"
                          className={['equipe2-tech-search-toggle', 'board-clickup-icon-btn-anchor', techSearchOpen ? 'is-active' : ''].filter(Boolean).join(' ')}
                          aria-label="Pesquisar técnicos"
                          aria-expanded={techSearchOpen}
                          onClick={() => setTechSearchOpen((open) => !open)}
                        >
                          <Search size={15.4} strokeWidth={2.2} aria-hidden />
                          <Equipe2Tooltip text="Pesquisar técnicos" position="left" />
                        </button>
                      </div>
                    </div>
                    {techSearchOpen ? (
                      <input
                        ref={techSearchInputRef}
                        className="equipe2-tech-search"
                        placeholder="Buscar técnico..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        aria-label="Buscar técnico"
                      />
                    ) : null}
                    <div className="equipe2-tech-hint">
                      <span>⠿</span>
                      <span>Arraste até uma área para vincular</span>
                    </div>
                  </div>
                  <div className="equipe2-tech-list">
                    {availableFiltered.map((tech) => {
                      const inactive = isTechInactive(tech);
                      return (
                      <div
                        key={tech.id}
                        className={['equipe2-tech-item', inactive ? 'is-inactive' : ''].filter(Boolean).join(' ')}
                        draggable={!inactive}
                        onDragStart={inactive ? undefined : handleDragStart('technician', tech.id)}
                        onDragEnd={inactive ? undefined : handleDragEnd}
                        title={tech.nome}
                      >
                        <div className="equipe2-tech-initials">{getInitials(tech.nome)}</div>
                        <span className="equipe2-tech-name">{tech.nome}</span>
                        {inactive ? <span className="equipe2-tech-inactive-tag">Inativo</span> : null}
                        <button
                          type="button"
                          className="equipe2-tech-item-manage board-clickup-icon-btn-anchor"
                          onClick={(e) => {
                            e.stopPropagation();
                            openTechDrawer(tech.id);
                          }}
                          aria-label={`Gerir técnico ${tech.nome}`}
                        >
                          <Settings2 size={13} strokeWidth={2} aria-hidden />
                          <Equipe2Tooltip text="Gerir técnico" position="left" />
                        </button>
                      </div>
                      );
                    })}
                    {availableFiltered.length === 0 && (
                      <div className="equipe2-empty">
                        {search.trim() ? 'Nenhum técnico encontrado.' : 'Todos os técnicos estão vinculados.'}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {confirm && (
        <ConfirmModal message={confirm.message} onConfirm={confirmYes} onCancel={() => setConfirm(null)} />
      )}

      {editModal && (
        <EditAreaModal
          name={editModal.name}
          abbr={editModal.abbr}
          error={editModal.error}
          onNameChange={(v) => setEditModal((m) => ({ ...m, name: v, error: '' }))}
          onAbbrChange={(v) => setEditModal((m) => ({ ...m, abbr: v }))}
          onSave={handleSaveEdit}
          onCancel={() => setEditModal(null)}
        />
      )}

      {hierarchyOpen && (
        <Equipe2HierarchyModal
          coordenadores={coordenadores}
          areas={areas}
          tecnicos={tecnicos}
          coordinatorPhotos={coordinatorPhotos}
          techFilters={techFilters}
          onTechFiltersChange={setTechFilters}
          filterContext={equipe2FilterContext}
          gestaoByCodigo={gestaoByCodigo}
          espLinks={espLinks}
          onClose={() => setHierarchyOpen(false)}
        />
      )}

      <Equipe2TechDetailDrawer
        open={!!techDrawer || (nativeMode && techForm?.mode === 'create')}
        tech={techForm?.mode === 'create' ? null : techDrawer}
        baseUrl={resolvedApiBase}
        areas={areas}
        coordenadores={coordenadores}
        allowProfile={nativeMode}
        profileMode={nativeMode ? (techForm?.mode === 'create' ? 'create' : (techDrawer ? 'edit' : null)) : null}
        onClose={() => {
          setTechDrawer(null);
          setTechForm(null);
        }}
        onSaved={(name) => {
          const created = techForm?.mode === 'create';
          refresh().then(() => toastSuccess(created ? `Técnico "${name}" cadastrado.` : `Dados de "${name}" atualizados.`));
        }}
      />
    </div>
  );
}

export default Equipe2View;
